import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  ProgressBar,
  Row,
  Spinner,
  Tab,
  Table,
  Tabs
} from 'react-bootstrap';
import {
  BULK_PROVIDERS,
  bulkFileReportToCsvParts,
  bulkResultsToCsvParts,
  bulkResultsToJsonlParts,
  MAX_BULK_FILES,
  runBulkFaceSearch,
  validateBulkFiles
} from '../services/bulkFaceSearchService';

const createInitialStatuses = (files, providerIds) => files.map((file, fileIndex) => ({
  fileIndex,
  fileName: file.name,
  fileSize: file.size,
  status: 'queued',
  matchCount: 0,
  providers: Object.fromEntries(
    providerIds.map(providerId => [providerId, {
      status: 'queued',
      matchCount: 0,
      error: ''
    }])
  )
}));

const statusVariant = status => ({
  queued: 'secondary',
  running: 'primary',
  completed: 'success',
  partial: 'warning',
  failed: 'danger',
  cancelled: 'secondary'
}[status] || 'secondary');

const formatBytes = bytes => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** unitIndex)).toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const sourceDomain = url => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url || 'No source URL';
  }
};

const safeHttpUrl = url => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
  } catch {
    return '';
  }
};

const downloadParts = (parts, type, fileName) => {
  const blob = new Blob(parts, { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const BulkFaceSearch = () => {
  const [files, setFiles] = useState([]);
  const [providerIds, setProviderIds] = useState(['thirdPartyAPI1']);
  const [similarityThreshold, setSimilarityThreshold] = useState(70);
  const [maxFaces, setMaxFaces] = useState(10);
  const [concurrency, setConcurrency] = useState(2);
  const [runState, setRunState] = useState('idle');
  const [fileStatuses, setFileStatuses] = useState([]);
  const [resultChunks, setResultChunks] = useState([]);
  const [selectionMessage, setSelectionMessage] = useState('');
  const [fatalError, setFatalError] = useState('');
  const [resultsTab, setResultsTab] = useState('matches');
  const [providerFilter, setProviderFilter] = useState('all');
  const [textFilter, setTextFilter] = useState('');
  const [minimumScore, setMinimumScore] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [resultPage, setResultPage] = useState(1);
  const [filePage, setFilePage] = useState(1);
  const abortControllerRef = useRef(null);

  const results = useMemo(() => resultChunks.flat(), [resultChunks]);
  const totalBytes = useMemo(
    () => files.reduce((total, file) => total + (Number(file.size) || 0), 0),
    [files]
  );

  const filteredResults = useMemo(() => {
    const query = textFilter.trim().toLowerCase();
    return results.filter(result => (
      (providerFilter === 'all' || result.providerId === providerFilter) &&
      result.nativeScore >= minimumScore &&
      (!query ||
        result.queryFileName.toLowerCase().includes(query) ||
        result.sourceUrl.toLowerCase().includes(query))
    ));
  }, [minimumScore, providerFilter, results, textFilter]);

  const resultPageCount = Math.max(1, Math.ceil(filteredResults.length / pageSize));
  const safeResultPage = Math.min(resultPage, resultPageCount);
  const visibleResults = filteredResults.slice(
    (safeResultPage - 1) * pageSize,
    safeResultPage * pageSize
  );

  const filePageSize = 50;
  const filePageCount = Math.max(1, Math.ceil(fileStatuses.length / filePageSize));
  const safeFilePage = Math.min(filePage, filePageCount);
  const visibleFileStatuses = fileStatuses.slice(
    (safeFilePage - 1) * filePageSize,
    safeFilePage * filePageSize
  );

  const completedFiles = fileStatuses.filter(status => (
    ['completed', 'partial', 'failed', 'cancelled'].includes(status.status)
  )).length;
  const failedFiles = fileStatuses.filter(status => status.status === 'failed').length;
  const partialFiles = fileStatuses.filter(status => status.status === 'partial').length;
  const progress = fileStatuses.length > 0
    ? Math.round((completedFiles / fileStatuses.length) * 100)
    : 0;
  const isRunning = runState === 'running';

  const handleFilesSelected = event => {
    const validation = validateBulkFiles(event.target.files, files);
    setFiles(current => [...current, ...validation.accepted]);
    setSelectionMessage(
      validation.rejected.length > 0
        ? `${validation.accepted.length} added; ${validation.rejected.length} rejected: ${validation.rejected
          .slice(0, 4)
          .map(item => `${item.fileName} (${item.reason})`)
          .join(', ')}${validation.rejected.length > 4 ? ', …' : ''}`
        : `${validation.accepted.length} image${validation.accepted.length === 1 ? '' : 's'} added.`
    );
    event.target.value = '';
  };

  const toggleProvider = providerId => {
    setProviderIds(current => current.includes(providerId)
      ? current.filter(id => id !== providerId)
      : [...current, providerId]);
  };

  const handleBulkEvent = event => {
    setFileStatuses(current => {
      const next = [...current];
      const existing = next[event.fileIndex];
      if (!existing) return current;

      const updated = {
        ...existing,
        providers: { ...existing.providers }
      };

      if (event.type === 'file-start') updated.status = 'running';
      if (event.type === 'file-cancelled') {
        updated.status = 'cancelled';
        updated.providers = Object.fromEntries(
          Object.entries(updated.providers).map(([providerId, providerStatus]) => [
            providerId,
            providerStatus.status === 'queued'
              ? { ...providerStatus, status: 'cancelled' }
              : providerStatus
          ])
        );
      }
      if (event.type === 'file-complete') {
        updated.status = event.status;
        updated.matchCount = event.matchCount;
      }

      if (event.providerId) {
        const providerStatus = {
          ...(updated.providers[event.providerId] || {})
        };
        if (event.type === 'provider-start') providerStatus.status = 'running';
        if (event.type === 'provider-complete') {
          providerStatus.status = 'completed';
          providerStatus.matchCount = event.matchCount;
          providerStatus.error = '';
        }
        if (event.type === 'provider-error') {
          providerStatus.status = 'failed';
          providerStatus.error = event.error;
        }
        if (event.type === 'provider-cancelled') providerStatus.status = 'cancelled';
        updated.providers[event.providerId] = providerStatus;
      }

      next[event.fileIndex] = updated;
      return next;
    });
  };

  const startSearch = async () => {
    if (files.length === 0 || providerIds.length === 0 || isRunning) return;

    const plannedSearches = files.length * providerIds.length;
    const confirmed = window.confirm(
      `Start ${plannedSearches} provider search${plannedSearches === 1 ? '' : 'es'} ` +
      `across ${files.length} image${files.length === 1 ? '' : 's'}? ` +
      'Paid provider credits may be consumed.'
    );
    if (!confirmed) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setFatalError('');
    setResultChunks([]);
    setFileStatuses(createInitialStatuses(files, providerIds));
    setResultPage(1);
    setFilePage(1);
    setResultsTab('matches');
    setRunState('running');

    try {
      const outcome = await runBulkFaceSearch({
        files,
        providerIds,
        maxFaces,
        similarityThreshold,
        concurrency,
        signal: controller.signal,
        onEvent: handleBulkEvent,
        onResults: chunk => setResultChunks(current => [...current, chunk])
      });
      if (outcome.cancelled) {
        setFileStatuses(current => current.map(fileStatus => (
          fileStatus.status === 'queued'
            ? {
              ...fileStatus,
              status: 'cancelled',
              providers: Object.fromEntries(
                Object.entries(fileStatus.providers).map(([providerId, providerStatus]) => [
                  providerId,
                  { ...providerStatus, status: 'cancelled' }
                ])
              )
            }
            : fileStatus
        )));
      }
      setRunState(outcome.cancelled ? 'cancelled' : 'completed');
    } catch (error) {
      if (controller.signal.aborted) {
        setFileStatuses(current => current.map(fileStatus => (
          fileStatus.status === 'queued'
            ? {
              ...fileStatus,
              status: 'cancelled',
              providers: Object.fromEntries(
                Object.entries(fileStatus.providers).map(([providerId, providerStatus]) => [
                  providerId,
                  { ...providerStatus, status: 'cancelled' }
                ])
              )
            }
            : fileStatus
        )));
        setRunState('cancelled');
      } else {
        setFatalError(error.message || 'Bulk search failed.');
        setRunState('failed');
      }
    } finally {
      abortControllerRef.current = null;
    }
  };

  const cancelSearch = () => {
    abortControllerRef.current?.abort();
  };

  const clearBatch = () => {
    setFiles([]);
    setFileStatuses([]);
    setResultChunks([]);
    setSelectionMessage('');
    setFatalError('');
    setRunState('idle');
    setResultPage(1);
    setFilePage(1);
  };

  const timestampedName = suffix => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `face-search-bulk-${timestamp}.${suffix}`;
  };

  return (
    <div>
      <Alert variant="info">
        <Alert.Heading>Bulk Face Search</Alert.Heading>
        <p className="mb-2">
          Add up to {MAX_BULK_FILES} JPEG or PNG files, select one or more sources, and run a
          throttled batch. Results appear incrementally and remain available if you cancel.
        </p>
        <small>
          Provider scores are kept as native scores and should not be compared directly across sources.
          CSV and JSONL exports contain every returned match; the file report also includes failures and
          zero-result searches.
        </small>
      </Alert>

      {fatalError && <Alert variant="danger">{fatalError}</Alert>}
      {selectionMessage && <Alert variant="secondary">{selectionMessage}</Alert>}

      <Row>
        <Col xl={4} className="mb-4">
          <Card className="h-100">
            <Card.Header><strong>1. Add query images</strong></Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>JPEG or PNG files</Form.Label>
                <Form.Control
                  type="file"
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                  multiple
                  onChange={handleFilesSelected}
                  disabled={isRunning}
                />
              </Form.Group>
              <div className="d-flex justify-content-between mb-2">
                <span>{files.length} files</span>
                <span>{formatBytes(totalBytes)}</span>
              </div>
              {files.length > 0 && (
                <div className="border rounded p-2 small" style={{ maxHeight: 180, overflowY: 'auto' }}>
                  {files.slice(0, 100).map((file, index) => (
                    <div className="d-flex justify-content-between gap-2" key={`${file.name}-${index}`}>
                      <span className="text-truncate" title={file.name}>{file.name}</span>
                      <span className="text-nowrap text-muted">{formatBytes(file.size)}</span>
                    </div>
                  ))}
                  {files.length > 100 && <div className="text-muted">…and {files.length - 100} more</div>}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} className="mb-4">
          <Card className="h-100">
            <Card.Header><strong>2. Select search source(s)</strong></Card.Header>
            <Card.Body>
              {BULK_PROVIDERS.map(provider => (
                <Form.Check
                  key={provider.id}
                  id={`bulk-provider-${provider.id}`}
                  type="checkbox"
                  className="mb-3"
                  checked={providerIds.includes(provider.id)}
                  disabled={isRunning}
                  onChange={() => toggleProvider(provider.id)}
                  label={(
                    <span>
                      <strong>{provider.label}</strong><br />
                      <small className="text-muted">{provider.description}</small>
                    </span>
                  )}
                />
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={4} className="mb-4">
          <Card className="h-100">
            <Card.Header><strong>3. Batch controls</strong></Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Concurrent images</Form.Label>
                <Form.Select
                  value={concurrency}
                  disabled={isRunning}
                  onChange={event => setConcurrency(Number(event.target.value))}
                >
                  <option value={1}>1 — safest</option>
                  <option value={2}>2 — recommended</option>
                  <option value={3}>3 — fastest</option>
                </Form.Select>
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Threshold</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={100}
                      value={similarityThreshold}
                      disabled={isRunning}
                      onChange={event => setSimilarityThreshold(Number(event.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Max faces</Form.Label>
                    <Form.Control
                      type="number"
                      min={1}
                      max={100}
                      value={maxFaces}
                      disabled={isRunning}
                      onChange={event => setMaxFaces(Number(event.target.value))}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-grid gap-2">
                <small className="text-muted text-center">
                  Planned provider searches: {files.length * providerIds.length}
                </small>
                <Button
                  variant="primary"
                  onClick={startSearch}
                  disabled={isRunning || files.length === 0 || providerIds.length === 0}
                >
                  {isRunning ? <><Spinner size="sm" className="me-2" />Searching batch…</> : 'Start Bulk Search'}
                </Button>
                {isRunning ? (
                  <Button variant="outline-danger" onClick={cancelSearch}>Cancel and keep results</Button>
                ) : (
                  <Button
                    variant="outline-secondary"
                    onClick={clearBatch}
                    disabled={files.length === 0 && results.length === 0}
                  >
                    Clear batch
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {fileStatuses.length > 0 && (
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
              <strong>{completedFiles} of {fileStatuses.length} files finished</strong>
              <span>
                <Badge bg="success" className="me-2">{results.length} matches</Badge>
                {partialFiles > 0 && <Badge bg="warning" text="dark" className="me-2">{partialFiles} partial</Badge>}
                {failedFiles > 0 && <Badge bg="danger">{failedFiles} failed</Badge>}
              </span>
            </div>
            <ProgressBar
              now={progress}
              label={`${progress}%`}
              animated={isRunning}
              variant={runState === 'failed' ? 'danger' : runState === 'cancelled' ? 'warning' : 'success'}
            />
          </Card.Body>
        </Card>
      )}

      {(fileStatuses.length > 0 || results.length > 0) && (
        <Card>
          <Card.Header>
            <Tabs activeKey={resultsTab} onSelect={key => setResultsTab(key || 'matches')}>
              <Tab eventKey="matches" title={`Matches (${results.length})`} />
              <Tab eventKey="files" title={`File status (${fileStatuses.length})`} />
            </Tabs>
          </Card.Header>
          <Card.Body>
            {resultsTab === 'matches' && (
              <>
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-end mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    <Form.Group>
                      <Form.Label className="small">Search results</Form.Label>
                      <Form.Control
                        type="search"
                        placeholder="File name or source URL"
                        value={textFilter}
                        onChange={event => { setTextFilter(event.target.value); setResultPage(1); }}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label className="small">Provider</Form.Label>
                      <Form.Select
                        value={providerFilter}
                        onChange={event => { setProviderFilter(event.target.value); setResultPage(1); }}
                      >
                        <option value="all">All providers</option>
                        {BULK_PROVIDERS.map(provider => (
                          <option value={provider.id} key={provider.id}>{provider.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label className="small">Minimum native score</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        value={minimumScore}
                        onChange={event => { setMinimumScore(Number(event.target.value)); setResultPage(1); }}
                        style={{ width: 130 }}
                      />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label className="small">Rows per page</Form.Label>
                      <Form.Select
                        value={pageSize}
                        onChange={event => { setPageSize(Number(event.target.value)); setResultPage(1); }}
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={250}>250</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="success"
                      disabled={results.length === 0}
                      onClick={() => downloadParts(
                        bulkResultsToCsvParts(results),
                        'text/csv;charset=utf-8',
                        timestampedName('csv')
                      )}
                    >
                      Export all CSV
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-success"
                      disabled={results.length === 0}
                      onClick={() => downloadParts(
                        bulkResultsToJsonlParts(results),
                        'application/x-ndjson;charset=utf-8',
                        timestampedName('jsonl')
                      )}
                    >
                      Export all JSONL
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={filteredResults.length === 0}
                      onClick={() => downloadParts(
                        bulkResultsToCsvParts(filteredResults),
                        'text/csv;charset=utf-8',
                        timestampedName('filtered.csv')
                      )}
                    >
                      Export filtered CSV
                    </Button>
                  </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                  <small>{filteredResults.length} matching rows</small>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={safeResultPage <= 1}
                      onClick={() => setResultPage(page => Math.max(1, page - 1))}
                    >Previous</Button>
                    <small>Page {safeResultPage} of {resultPageCount}</small>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={safeResultPage >= resultPageCount}
                      onClick={() => setResultPage(page => Math.min(resultPageCount, page + 1))}
                    >Next</Button>
                  </div>
                </div>

                <Table responsive hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Preview</th>
                      <th>Query file</th>
                      <th>Provider</th>
                      <th>Native score</th>
                      <th>Source</th>
                      <th>Metadata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleResults.map(result => {
                      const sourceUrl = safeHttpUrl(result.sourceUrl);
                      const thumbnailUrl = safeHttpUrl(result.thumbnailUrl);
                      return (
                      <tr key={result.id}>
                        <td style={{ width: 72 }}>
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt="Face match"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }}
                            />
                          ) : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          <span title={result.queryFileName}>{result.queryFileName}</span><br />
                          <small className="text-muted">{formatBytes(result.queryFileSize)}</small>
                        </td>
                        <td>{result.providerLabel}</td>
                        <td>{result.nativeScore.toFixed(1)}</td>
                        <td style={{ maxWidth: 220 }}>
                          {sourceUrl ? (
                            <a href={sourceUrl} target="_blank" rel="noopener noreferrer" title={sourceUrl}>
                              {sourceDomain(sourceUrl)}
                            </a>
                          ) : <span className="text-muted">Unavailable</span>}
                        </td>
                        <td style={{ maxWidth: 260 }}>
                          <small className="text-muted text-break">
                            {JSON.stringify(result.metadata).slice(0, 180)}
                            {JSON.stringify(result.metadata).length > 180 ? '…' : ''}
                          </small>
                        </td>
                      </tr>
                      );
                    })}
                    {visibleResults.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted py-4">No matching results.</td></tr>
                    )}
                  </tbody>
                </Table>
              </>
            )}

            {resultsTab === 'files' && (
              <>
                <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                  <Button
                    size="sm"
                    variant="success"
                    disabled={fileStatuses.length === 0}
                    onClick={() => downloadParts(
                      bulkFileReportToCsvParts(fileStatuses),
                      'text/csv;charset=utf-8',
                      timestampedName('file-report.csv')
                    )}
                  >
                    Export complete file report
                  </Button>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={safeFilePage <= 1}
                      onClick={() => setFilePage(page => Math.max(1, page - 1))}
                    >Previous</Button>
                    <small>Page {safeFilePage} of {filePageCount}</small>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={safeFilePage >= filePageCount}
                      onClick={() => setFilePage(page => Math.min(filePageCount, page + 1))}
                    >Next</Button>
                  </div>
                </div>

                <Table responsive hover size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Query file</th>
                      <th>Overall status</th>
                      <th>Matches</th>
                      <th>Provider details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFileStatuses.map(fileStatus => (
                      <tr key={`${fileStatus.fileIndex}-${fileStatus.fileName}`}>
                        <td>{fileStatus.fileName}</td>
                        <td><Badge bg={statusVariant(fileStatus.status)}>{fileStatus.status}</Badge></td>
                        <td>{fileStatus.matchCount || 0}</td>
                        <td>
                          {Object.entries(fileStatus.providers).map(([providerId, providerStatus]) => (
                            <div key={providerId} className="mb-1">
                              <Badge bg={statusVariant(providerStatus.status)} className="me-2">
                                {BULK_PROVIDERS.find(provider => provider.id === providerId)?.label || providerId}
                              </Badge>
                              <small>
                                {providerStatus.status}
                                {providerStatus.matchCount ? ` — ${providerStatus.matchCount} matches` : ''}
                                {providerStatus.error ? ` — ${providerStatus.error}` : ''}
                              </small>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default BulkFaceSearch;
